// src/config/defaults.ts
var DEFAULT_PLATFORM_URL = "https://platform.feedscription.com";

// src/browser/api-request.ts
var ApiRequestError = class extends Error {
  constructor(status, response) {
    super(response.message || `API Error: ${status}`);
    this.name = "ApiRequestError";
    this.status = status;
    this.response = response;
  }
};
function createApiRequest(client) {
  return async function apiRequest2(url, init2, options = {}) {
    const { autoLogin = true, autoPayment = true, maxRetries = 1 } = options;
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      const headers = new Headers(init2?.headers);
      if (options.headers) {
        for (const [key, value] of Object.entries(options.headers)) {
          headers.set(key, value);
        }
      }
      if (!headers.has("Content-Type") && init2?.body) {
        headers.set("Content-Type", "application/json");
      }
      const controller = options.timeout ? new AbortController() : void 0;
      const timeoutId = options.timeout ? setTimeout(() => controller.abort(), options.timeout) : void 0;
      try {
        const response = await fetch(url, {
          ...init2,
          headers,
          signal: controller?.signal
        });
        if (timeoutId) clearTimeout(timeoutId);
        if (response.status === 401 && autoLogin && client.isBrowser()) {
          try {
            const data = await response.clone().json();
            if (data.action === "show_login_popup") {
              await client.showLoginPopup();
              continue;
            }
          } catch {
            await client.showLoginPopup();
            continue;
          }
        }
        if (response.status === 402 && autoPayment && client.isBrowser()) {
          try {
            const data = await response.clone().json();
            if (data.action === "show_payment_popup") {
              await client.showPaymentPopup();
              continue;
            }
          } catch {
            await client.showPaymentPopup();
            continue;
          }
        }
        if (!response.ok) {
          let errorResponse;
          try {
            errorResponse = await response.json();
          } catch {
            errorResponse = {
              code: "UNKNOWN_ERROR",
              message: `HTTP ${response.status}: ${response.statusText}`
            };
          }
          throw new ApiRequestError(response.status, errorResponse);
        }
        const contentType = response.headers.get("content-type");
        if (contentType?.includes("application/json")) {
          return await response.json();
        }
        return await response.text();
      } catch (err) {
        if (timeoutId) clearTimeout(timeoutId);
        if (err instanceof ApiRequestError) {
          throw err;
        }
        if (err instanceof Error && err.name === "AbortError") {
          throw new ApiRequestError(408, {
            code: "TIMEOUT",
            message: "Request timeout"
          });
        }
        throw new ApiRequestError(0, {
          code: "NETWORK_ERROR",
          message: err instanceof Error ? err.message : "Network error"
        });
      }
    }
    throw new ApiRequestError(0, {
      code: "MAX_RETRIES_EXCEEDED",
      message: "Max retries exceeded"
    });
  };
}

// src/browser/index.ts
var WORTOU_TOKEN_KEY = "wortou_user_token";
var WORTOU_REFRESH_TOKEN_KEY = "wortou_refresh_token";
var refreshPromise = null;
var paymentPromise = null;
var WORTOU_PAYMENT_PENDING_KEY = "wortou_payment_pending";
var WORTOU_PAYMENT_COMPLETED_KEY = "wortou_payment_completed";
function getUserToken() {
  try {
    const token = localStorage.getItem(WORTOU_TOKEN_KEY);
    if (process.env.NODE_ENV === "development" || typeof window !== "undefined" && window.__WORTOU_DEBUG__) {
      console.log("[Wortou Debug] getUserToken:", token ? `${token.substring(0, 20)}...` : "null");
    }
    return token;
  } catch (e) {
    console.error("[Wortou] Failed to get token from localStorage:", e);
    return null;
  }
}
function setUserToken(token) {
  try {
    localStorage.setItem(WORTOU_TOKEN_KEY, token);
    if (process.env.NODE_ENV === "development" || typeof window !== "undefined" && window.__WORTOU_DEBUG__) {
      console.log("[Wortou Debug] setUserToken: Token stored successfully", token.substring(0, 20) + "...");
    }
  } catch (e) {
    console.error("[Wortou] Failed to store token in localStorage:", e);
  }
}
function getRefreshToken() {
  try {
    return localStorage.getItem(WORTOU_REFRESH_TOKEN_KEY);
  } catch {
    return null;
  }
}
function setRefreshToken(token) {
  try {
    localStorage.setItem(WORTOU_REFRESH_TOKEN_KEY, token);
  } catch {
  }
}
function clearUserToken() {
  try {
    localStorage.removeItem(WORTOU_TOKEN_KEY);
    localStorage.removeItem(WORTOU_REFRESH_TOKEN_KEY);
  } catch {
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("wortou:auth:changed", {
      detail: { type: "logout" }
    }));
  }
}
function parseJWT(token) {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(decoded);
  } catch {
    return null;
  }
}
function isTokenExpiringSoon(token, bufferSeconds = 60) {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return true;
    const payload = JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));
    const exp = payload.exp;
    if (!exp) return false;
    const now = Math.floor(Date.now() / 1e3);
    return exp <= now + bufferSeconds;
  } catch {
    return true;
  }
}
function isTokenExpired(token) {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return true;
    const payload = JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));
    const exp = payload.exp;
    if (!exp) return false;
    const now = Math.floor(Date.now() / 1e3);
    return exp <= now;
  } catch {
    return true;
  }
}
async function refreshAccessToken() {
  if (refreshPromise) {
    console.log("[Wortou] Refresh already in progress, waiting for result...");
    return refreshPromise;
  }
  refreshPromise = performRefreshAccessToken();
  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}
async function performRefreshAccessToken() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    console.log("[Wortou] No refresh token available");
    return null;
  }
  const clientId = globalConfig.clientId;
  if (!clientId) {
    console.log("[Wortou] No clientId configured, cannot refresh token");
    return null;
  }
  try {
    console.log("[Wortou] Attempting to refresh access token...");
    const response = await fetch(globalConfig.oauthTokenUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        grant_type: "refresh_token",
        refresh_token: refreshToken,
        client_id: clientId
      })
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: "unknown_error" }));
      console.log("[Wortou] Token refresh failed:", error);
      if (response.status === 400 || response.status === 401) {
        try {
          localStorage.removeItem(WORTOU_REFRESH_TOKEN_KEY);
        } catch {
        }
      }
      return null;
    }
    const tokenResponse = await response.json();
    setUserToken(tokenResponse.access_token);
    if (tokenResponse.refresh_token) {
      setRefreshToken(tokenResponse.refresh_token);
    }
    console.log("[Wortou] Token refreshed successfully");
    return tokenResponse.access_token;
  } catch (error) {
    console.error("[Wortou] Token refresh error:", error);
    return null;
  }
}
async function ensureValidToken() {
  const token = getUserToken();
  if (!token) {
    return null;
  }
  if (isTokenExpiringSoon(token, 60)) {
    console.log("[Wortou] Token is expiring soon, attempting refresh...");
    const newToken = await refreshAccessToken();
    if (newToken) {
      return newToken;
    }
    if (isTokenExpired(token)) {
      console.log("[Wortou] Token has expired and refresh failed, clearing token");
      clearUserToken();
      return null;
    }
    return token;
  }
  return token;
}
function getGameIdFromToken() {
  const token = getUserToken();
  console.log("[Wortou Debug] getGameIdFromToken - token exists:", !!token);
  if (!token) return null;
  try {
    const parts = token.split(".");
    if (parts.length !== 3) {
      console.log("[Wortou Debug] getGameIdFromToken - invalid JWT format, parts:", parts.length);
      return null;
    }
    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    const data = JSON.parse(decoded);
    console.log("[Wortou Debug] getGameIdFromToken - JWT payload:", {
      game_id: data.game_id,
      user_id: data.user_id,
      sub: data.sub,
      exp: data.exp
    });
    return typeof data.game_id === "number" ? data.game_id : null;
  } catch (e) {
    console.log("[Wortou Debug] getGameIdFromToken - parse error:", e);
    return null;
  }
}
function isLoggedIn() {
  return !!getUserToken();
}
async function checkLoginStatus() {
  await autoHandleAllCallbacks();
  const token = getUserToken();
  if (!token) {
    console.log("[Wortou] checkLoginStatus: No token found");
    return false;
  }
  if (isTokenExpired(token)) {
    console.log("[Wortou] checkLoginStatus: Token has expired");
    const refreshToken = getRefreshToken();
    if (refreshToken) {
      console.log("[Wortou] checkLoginStatus: Attempting to refresh token...");
      const newToken = await refreshAccessToken();
      if (newToken) {
        console.log("[Wortou] checkLoginStatus: Token refreshed successfully");
        return true;
      }
    }
    console.log("[Wortou] checkLoginStatus: Failed to refresh, clearing invalid token");
    clearUserToken();
    return false;
  }
  if (isTokenExpiringSoon(token, 60)) {
    console.log("[Wortou] checkLoginStatus: Token expiring soon, attempting refresh...");
    const newToken = await refreshAccessToken();
    if (newToken) {
      console.log("[Wortou] checkLoginStatus: Token refreshed proactively");
    }
  }
  console.log("[Wortou] checkLoginStatus: Token is valid");
  return true;
}
function handleRedirectCallback(options = {}) {
  const { cleanUrl = true } = options;
  if (typeof window === "undefined") {
    return null;
  }
  try {
    const hash = window.location.hash;
    if (!hash || hash.length <= 1) {
      return null;
    }
    const fragmentParams = new URLSearchParams(hash.substring(1));
    const token = fragmentParams.get("wortou_token");
    if (token) {
      setUserToken(token);
      if (cleanUrl) {
        const cleanedUrl = window.location.href.split("#")[0];
        window.history.replaceState({}, "", cleanedUrl);
      }
      return token;
    }
    return null;
  } catch {
    return null;
  }
}
var callbackHandled = false;
var callbackHandlingPromise = null;
async function autoHandleAllCallbacks() {
  if (typeof window === "undefined") {
    return;
  }
  if (callbackHandlingPromise) {
    return callbackHandlingPromise;
  }
  if (callbackHandled) {
    return;
  }
  callbackHandlingPromise = (async () => {
    try {
      await new Promise((resolve) => setTimeout(resolve, 0));
      const url = new URL(window.location.href);
      if (url.searchParams.has("code") && url.searchParams.has("state")) {
        await handleOAuthCallback();
        callbackHandled = true;
        return;
      }
      const result = handleRedirectCallback();
      if (result) {
        callbackHandled = true;
      }
    } finally {
      callbackHandlingPromise = null;
    }
  })();
  return callbackHandlingPromise;
}
autoHandleAllCallbacks();
cleanupPaymentParams();
function cleanupPaymentParams() {
  if (typeof window === "undefined") {
    return;
  }
  try {
    const url = new URL(window.location.href);
    const paymentCompleted = url.searchParams.get(WORTOU_PAYMENT_COMPLETED_KEY);
    if (paymentCompleted) {
      console.log("[Wortou] Detected payment return, cleaning up params");
      url.searchParams.delete(WORTOU_PAYMENT_COMPLETED_KEY);
      window.history.replaceState({}, "", url.toString());
      try {
        sessionStorage.removeItem(WORTOU_PAYMENT_PENDING_KEY);
        sessionStorage.removeItem("wortou_redirect_return");
      } catch {
      }
    }
  } catch (e) {
    console.warn("[Wortou] Failed to cleanup payment params:", e);
  }
}
function handleLoginCallback() {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get("token");
    if (token) {
      setUserToken(token);
      urlParams.delete("token");
      const newUrl = urlParams.toString() ? `${window.location.pathname}?${urlParams.toString()}${window.location.hash}` : `${window.location.pathname}${window.location.hash}`;
      window.history.replaceState({}, "", newUrl);
      return true;
    }
  } catch (e) {
    console.error("[Wortou] Failed to handle login callback", e);
  }
  return false;
}
function getLoginUrl(returnUrl) {
  console.warn(
    "[Wortou] \u26A0\uFE0F getLoginUrl() \u5DF2\u5E9F\u5F03\uFF01\u6B64\u65B9\u5F0F\u65E0\u6CD5\u83B7\u53D6 SDK token\u3002\n\u8BF7\u6539\u7528 startOAuthLogin() \u914D\u5408 onClick \u4E8B\u4EF6\uFF1A\n  <button onClick={() => startOAuthLogin()}>\u767B\u5F55</button>\n\u53C2\u8003\u6587\u6863\uFF1Ahttps://docs.wortou.com/sdk/browser#oauth-login"
  );
  const url = returnUrl || window.location.href;
  return `${globalConfig.loginUrl}?return_url=${encodeURIComponent(url)}`;
}
async function getOAuthLoginUrl(options = {}) {
  const { clientId, oauthAuthorizeUrl, oauthScope } = globalConfig;
  if (!clientId) {
    throw new Error('[Wortou] clientId is required for OAuth login. Call configure({ clientId: "your-game-id" }) first.');
  }
  const redirectUri = options.redirectUri || window.location.href.split("?")[0].split("#")[0];
  const scope = options.scope || oauthScope;
  const codeVerifier = generateRandomString(64);
  const codeChallenge = await generateCodeChallenge(codeVerifier);
  const state = generateRandomString(32);
  savePKCEState({
    codeVerifier,
    state,
    redirectUri,
    timestamp: Date.now()
  });
  const authUrl = new URL(oauthAuthorizeUrl);
  authUrl.searchParams.set("response_type", "code");
  authUrl.searchParams.set("client_id", clientId);
  authUrl.searchParams.set("redirect_uri", redirectUri);
  authUrl.searchParams.set("scope", scope);
  authUrl.searchParams.set("state", state);
  authUrl.searchParams.set("code_challenge", codeChallenge);
  authUrl.searchParams.set("code_challenge_method", "S256");
  return authUrl.toString();
}
var DEFAULT_APP_URL = DEFAULT_PLATFORM_URL;
var HIDE_BILLING = false;
var DEFAULT_CONFIG = {
  platformUrl: DEFAULT_APP_URL,
  loginUrl: `${DEFAULT_APP_URL}/login`,
  paymentUrl: `${DEFAULT_APP_URL}/pay/quick`,
  timeout: 6e4,
  autoLogin: true,
  // When HIDE_BILLING is true, disable automatic payment popups
  autoPayment: HIDE_BILLING ? false : true,
  maxRetries: 1,
  popupMode: "popup",
  onPopupBlocked: () => {
  },
  // noop by default
  // API 配置（默认使用 BFF 代理）
  apiBaseUrl: "/api/wortou",
  // OAuth 配置
  clientId: "",
  oauthAuthorizeUrl: `${DEFAULT_APP_URL}/api/v1/oauth/authorize`,
  oauthTokenUrl: `${DEFAULT_APP_URL}/api/v1/oauth/token`,
  oauthScope: "consume balance"
};
var globalConfig = { ...DEFAULT_CONFIG };
function configure(config) {
  if (config.platformUrl) {
    const baseUrl = config.platformUrl.replace(/\/$/, "");
    const autoConfig = {
      platformUrl: baseUrl,
      loginUrl: config.loginUrl || `${baseUrl}/login`,
      paymentUrl: config.paymentUrl || `${baseUrl}/pay/quick`,
      apiBaseUrl: config.apiBaseUrl || `${baseUrl}/api/v1`,
      oauthAuthorizeUrl: config.oauthAuthorizeUrl || `${baseUrl}/api/v1/oauth/authorize`,
      oauthTokenUrl: config.oauthTokenUrl || `${baseUrl}/api/v1/oauth/token`
    };
    globalConfig = { ...globalConfig, ...autoConfig, ...config };
  } else {
    globalConfig = { ...globalConfig, ...config };
  }
}
function init(gameId, options) {
  if (!gameId) {
    console.error("[Wortou] init() requires a gameId. Get your game ID from the platform dashboard.");
    return;
  }
  configure({ clientId: gameId, ...options });
  console.log("[Wortou] SDK initialized for game:", gameId);
}
function resetConfig() {
  globalConfig = { ...DEFAULT_CONFIG };
}
function getConfig() {
  return globalConfig;
}
var PKCE_STORAGE_KEY = "wortou_pkce_state";
function generateRandomString(length) {
  const charset = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~";
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => charset[byte % charset.length]).join("");
}
async function generateCodeChallenge(codeVerifier) {
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const digest = await crypto.subtle.digest("SHA-256", data);
  const base64 = btoa(String.fromCharCode(...new Uint8Array(digest)));
  return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
var PKCE_BACKUP_KEY = "wortou_pkce_state_backup";
function savePKCEState(state) {
  const stateJson = JSON.stringify(state);
  try {
    sessionStorage.setItem(PKCE_STORAGE_KEY, stateJson);
    console.log("[Wortou] PKCE state saved to sessionStorage");
  } catch (e) {
    console.warn("[Wortou] Failed to save PKCE state to sessionStorage:", e);
  }
  try {
    localStorage.setItem(PKCE_BACKUP_KEY, stateJson);
    console.log("[Wortou] PKCE state backed up to localStorage");
  } catch {
  }
}
function consumePKCEState() {
  let stored = null;
  let source = "";
  try {
    stored = sessionStorage.getItem(PKCE_STORAGE_KEY);
    if (stored) {
      sessionStorage.removeItem(PKCE_STORAGE_KEY);
      source = "sessionStorage";
    }
  } catch {
  }
  if (!stored) {
    try {
      stored = localStorage.getItem(PKCE_BACKUP_KEY);
      if (stored) {
        source = "localStorage (backup)";
      }
    } catch {
    }
  }
  try {
    localStorage.removeItem(PKCE_BACKUP_KEY);
  } catch {
  }
  if (!stored) {
    console.log("[Wortou] PKCE state not found in either sessionStorage or localStorage");
    return null;
  }
  try {
    const state = JSON.parse(stored);
    if (Date.now() - state.timestamp > 10 * 60 * 1e3) {
      console.log("[Wortou] PKCE state expired (> 10 minutes)");
      return null;
    }
    console.log("[Wortou] PKCE state retrieved from", source);
    return state;
  } catch {
    console.log("[Wortou] Failed to parse PKCE state");
    return null;
  }
}
async function startOAuthLogin(options = {}) {
  const { clientId, oauthAuthorizeUrl, oauthScope } = globalConfig;
  if (!clientId) {
    throw new Error('[Wortou] clientId is required for OAuth login. Call configure({ clientId: "your-game-id" }) first.');
  }
  const redirectUri = options.redirectUri || window.location.href.split("?")[0].split("#")[0];
  const scope = options.scope || oauthScope;
  const codeVerifier = generateRandomString(64);
  const codeChallenge = await generateCodeChallenge(codeVerifier);
  const state = generateRandomString(32);
  savePKCEState({
    codeVerifier,
    state,
    redirectUri,
    timestamp: Date.now()
  });
  const authUrl = new URL(oauthAuthorizeUrl);
  authUrl.searchParams.set("response_type", "code");
  authUrl.searchParams.set("client_id", clientId);
  authUrl.searchParams.set("redirect_uri", redirectUri);
  authUrl.searchParams.set("scope", scope);
  authUrl.searchParams.set("state", state);
  authUrl.searchParams.set("code_challenge", codeChallenge);
  authUrl.searchParams.set("code_challenge_method", "S256");
  window.location.href = authUrl.toString();
}
async function exchangeCodeForToken(code, codeVerifier, redirectUri) {
  const { clientId, oauthTokenUrl } = globalConfig;
  const response = await fetch(oauthTokenUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      grant_type: "authorization_code",
      code,
      client_id: clientId,
      code_verifier: codeVerifier,
      redirect_uri: redirectUri
    })
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "unknown_error" }));
    throw new Error(`Token exchange failed: ${error.error_description || error.error}`);
  }
  return response.json();
}
async function handleOAuthCallback(options = {}) {
  const { cleanUrl = true } = options;
  if (typeof window === "undefined") {
    return null;
  }
  try {
    const url = new URL(window.location.href);
    const code = url.searchParams.get("code");
    const state = url.searchParams.get("state");
    const error = url.searchParams.get("error");
    if (error) {
      const errorDescription = url.searchParams.get("error_description") || error;
      console.error("[Wortou] OAuth error:", errorDescription);
      if (cleanUrl) {
        url.searchParams.delete("error");
        url.searchParams.delete("error_description");
        url.searchParams.delete("state");
        window.history.replaceState({}, "", url.toString());
      }
      return null;
    }
    if (!code || !state) {
      return null;
    }
    console.log("[Wortou] OAuth callback detected, code:", code.substring(0, 10) + "...", "state:", state.substring(0, 10) + "...");
    const pkceState = consumePKCEState();
    if (!pkceState) {
      console.warn(
        "[Wortou] PKCE state not found in sessionStorage.\n\u53EF\u80FD\u539F\u56E0\uFF1A\n1. \u4F7F\u7528\u4E86 getLoginUrl() \u6216\u76F4\u63A5\u8DF3\u8F6C\u5230 /login \u9875\u9762\uFF08\u5E94\u4F7F\u7528 startOAuthLogin()\uFF09\n2. sessionStorage \u88AB\u6E05\u7A7A\u6216\u8DE8\u57DF\u8BBF\u95EE\n3. PKCE state \u5DF2\u8FC7\u671F\uFF08\u8D85\u8FC7 10 \u5206\u949F\uFF09\n4. \u8BE5\u56DE\u8C03\u5DF2\u88AB\u5904\u7406\u8FC7\uFF08\u5237\u65B0\u9875\u9762\uFF09\n\u6B63\u5728\u6E05\u7406 URL \u4E2D\u7684 OAuth \u53C2\u6570..."
      );
      if (cleanUrl) {
        url.searchParams.delete("code");
        url.searchParams.delete("state");
        window.history.replaceState({}, "", url.toString());
      }
      return null;
    }
    console.log("[Wortou] PKCE state found, validating...");
    if (pkceState.state !== state) {
      console.error("[Wortou] \u274C State mismatch - possible CSRF attack or stale callback");
      console.error("  Expected:", pkceState.state.substring(0, 10) + "...");
      console.error("  Received:", state.substring(0, 10) + "...");
      return null;
    }
    const tokenResponse = await exchangeCodeForToken(
      code,
      pkceState.codeVerifier,
      pkceState.redirectUri
    );
    setUserToken(tokenResponse.access_token);
    if (tokenResponse.refresh_token) {
      setRefreshToken(tokenResponse.refresh_token);
    }
    if (cleanUrl) {
      url.searchParams.delete("code");
      url.searchParams.delete("state");
      window.history.replaceState({}, "", url.toString());
    }
    const payload = parseJWT(tokenResponse.access_token);
    window.dispatchEvent(new CustomEvent("wortou:auth:changed", {
      detail: { type: "login", userId: payload?.user_id }
    }));
    console.log("[Wortou] OAuth login successful");
    return tokenResponse.access_token;
  } catch (error) {
    console.error("[Wortou] OAuth callback handling failed:", error);
    if (cleanUrl) {
      try {
        const url = new URL(window.location.href);
        if (url.searchParams.has("code") || url.searchParams.has("state")) {
          url.searchParams.delete("code");
          url.searchParams.delete("state");
          window.history.replaceState({}, "", url.toString());
          console.log("[Wortou] Cleaned up OAuth params from URL after error");
        }
      } catch {
      }
    }
    return null;
  }
}
var ApiRequestError2 = class extends Error {
  constructor(status, response) {
    super(response.message || `API Error: ${status}`);
    this.name = "ApiRequestError";
    this.status = status;
    this.response = response;
    Object.setPrototypeOf(this, new.target.prototype);
  }
};
var preOpenedWindowRef = null;
function preOpenWindow(options = {}) {
  const { width = 500, height = 600 } = options;
  const mode = globalConfig.popupMode;
  if (mode === "redirect") {
    return null;
  }
  if (mode === "tab") {
    preOpenedWindowRef = window.open("about:blank", "_blank");
  } else {
    const left = (window.innerWidth - width) / 2 + window.screenX;
    const top = (window.innerHeight - height) / 2 + window.screenY;
    preOpenedWindowRef = window.open(
      "about:blank",
      "wortou_window",
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes`
    );
  }
  return preOpenedWindowRef;
}
function closePreOpenedWindow() {
  if (preOpenedWindowRef && !preOpenedWindowRef.closed) {
    preOpenedWindowRef.close();
  }
  preOpenedWindowRef = null;
}
function consumePreOpenedWindow() {
  const win = preOpenedWindowRef;
  preOpenedWindowRef = null;
  return win;
}
function openWindow(url, name, options = {}) {
  const { width = 500, height = 600 } = options;
  const mode = globalConfig.popupMode;
  const preOpened = consumePreOpenedWindow();
  if (preOpened && !preOpened.closed) {
    preOpened.location.href = url;
    return preOpened;
  }
  let popup = null;
  if (mode === "redirect") {
    sessionStorage.setItem("wortou_redirect_return", window.location.href);
    window.location.href = url;
    return null;
  } else if (mode === "tab") {
    popup = window.open(url, "_blank");
  } else {
    const left = (window.innerWidth - width) / 2 + window.screenX;
    const top = (window.innerHeight - height) / 2 + window.screenY;
    popup = window.open(
      url,
      name,
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes`
    );
  }
  return popup;
}
function openLoginPopupWithToken(options = {}) {
  const { width = 500, height = 600, timeout = 5 * 60 * 1e3 } = options;
  return new Promise(async (resolve, reject) => {
    if (!globalConfig.clientId) {
      console.error(`
[Wortou] \u274C \u672A\u914D\u7F6E\u5E94\u7528 ID (clientId)

\u8BF7\u5728\u4F7F\u7528 SDK \u524D\u521D\u59CB\u5316\uFF1A

  import { init } from 'wortou/browser';
  init('your-game-id');  // \u4ECE\u5E73\u53F0\u540E\u53F0\u83B7\u53D6\u4F60\u7684\u5E94\u7528 ID

\u6216\u8005\u4F7F\u7528 configure\uFF1A

  import { configure } from 'wortou/browser';
  configure({ clientId: 'your-game-id' });
`);
      reject(new ApiRequestError2(0, {
        code: "MISSING_CLIENT_ID",
        message: '\u8BF7\u5148\u521D\u59CB\u5316 SDK\uFF1Ainit("your-game-id")'
      }));
      return;
    }
    const codeVerifier = generateRandomString(64);
    const codeChallenge = await generateCodeChallenge(codeVerifier);
    const state = generateRandomString(32);
    const authUrl = new URL(globalConfig.oauthAuthorizeUrl);
    authUrl.searchParams.set("response_type", "code");
    authUrl.searchParams.set("client_id", globalConfig.clientId);
    const isRedirectMode = globalConfig.popupMode === "redirect";
    const popupCallbackUrl = new URL("/oauth/popup-callback", new URL(globalConfig.oauthAuthorizeUrl).origin).toString();
    const userRedirectUri = window.location.href.split("?")[0].split("#")[0];
    const redirectUri = isRedirectMode ? userRedirectUri : popupCallbackUrl;
    authUrl.searchParams.set("redirect_uri", redirectUri);
    authUrl.searchParams.set("scope", globalConfig.oauthScope);
    authUrl.searchParams.set("state", state);
    authUrl.searchParams.set("code_challenge", codeChallenge);
    authUrl.searchParams.set("code_challenge_method", "S256");
    authUrl.searchParams.set("origin_url", window.location.href.split("#")[0]);
    console.log("[Wortou] Opening OAuth popup:", authUrl.toString());
    const popup = openWindow(authUrl.toString(), "wortou_login", { width, height });
    if (isRedirectMode) {
      savePKCEState({
        codeVerifier,
        state,
        redirectUri: userRedirectUri,
        timestamp: Date.now()
      });
      resolve(null);
      return;
    }
    if (!popup) {
      globalConfig.onPopupBlocked(authUrl.toString(), "login");
      reject(new ApiRequestError2(0, {
        code: "POPUP_BLOCKED",
        message: "\u5F39\u7A97\u88AB\u6D4F\u89C8\u5668\u963B\u6B62\u3002\u8BF7\u70B9\u51FB\u9875\u9762\u4E0A\u7684\u767B\u5F55\u6309\u94AE\uFF0C\u6216\u5141\u8BB8\u5F39\u7A97\u540E\u91CD\u8BD5",
        action: "show_login_popup"
      }));
      return;
    }
    let resolved = false;
    let timer;
    let timeoutId;
    const messageHandler = async (event) => {
      const data = event.data;
      if (data?.type === "wortou:oauth:code") {
        if (data.state !== state) {
          console.error("[Wortou] OAuth state mismatch - possible CSRF attack");
          return;
        }
        resolved = true;
        cleanup();
        try {
          console.log("[Wortou] Exchanging authorization code for token...");
          const tokenResponse = await exchangeCodeForToken(
            data.code,
            codeVerifier,
            popupCallbackUrl
          );
          setUserToken(tokenResponse.access_token);
          if (tokenResponse.refresh_token) {
            setRefreshToken(tokenResponse.refresh_token);
          }
          const payload = parseJWT(tokenResponse.access_token);
          window.dispatchEvent(new CustomEvent("wortou:auth:changed", {
            detail: { type: "login", userId: payload?.user_id }
          }));
          console.log("[Wortou] OAuth login successful");
          resolve(tokenResponse.access_token);
        } catch (error) {
          console.error("[Wortou] Token exchange failed:", error);
          reject(new ApiRequestError2(0, {
            code: "TOKEN_EXCHANGE_FAILED",
            message: error instanceof Error ? error.message : "Token \u4EA4\u6362\u5931\u8D25"
          }));
        }
        return;
      }
      if (data?.type === "wortou:oauth:error") {
        resolved = true;
        cleanup();
        reject(new ApiRequestError2(401, {
          code: data.error?.code || "OAUTH_ERROR",
          message: data.error?.message || "\u6388\u6743\u5931\u8D25"
        }));
        return;
      }
      const isAuthSuccess = data?.type === "wortou:auth:success" || data?.type === "wortou_auth" || data?.type === "wortou_auth_success";
      if (isAuthSuccess && data.token) {
        resolved = true;
        cleanup();
        setUserToken(data.token);
        console.log("[Wortou] Token received from popup (legacy mode)");
        resolve(data.token);
      }
    };
    let storageHandler = null;
    const cleanup = () => {
      window.removeEventListener("message", messageHandler);
      if (storageHandler) {
        window.removeEventListener("storage", storageHandler);
      }
      clearInterval(timer);
      clearTimeout(timeoutId);
      try {
        localStorage.removeItem(`wortou_oauth_result_${state}`);
      } catch {
      }
      if (popup && !popup.closed) {
        popup.close();
      }
    };
    const checkLocalStorageResult = async () => {
      try {
        const resultKey = `wortou_oauth_result_${state}`;
        const resultStr = localStorage.getItem(resultKey);
        if (resultStr) {
          const result = JSON.parse(resultStr);
          if (result.type === "wortou:oauth:code" && result.code && result.state === state) {
            console.log("[Wortou] Found OAuth result in localStorage (fallback mode)");
            localStorage.removeItem(resultKey);
            resolved = true;
            cleanup();
            try {
              const tokenResponse = await exchangeCodeForToken(
                result.code,
                codeVerifier,
                popupCallbackUrl
              );
              setUserToken(tokenResponse.access_token);
              if (tokenResponse.refresh_token) {
                setRefreshToken(tokenResponse.refresh_token);
              }
              const payload = parseJWT(tokenResponse.access_token);
              window.dispatchEvent(new CustomEvent("wortou:auth:changed", {
                detail: { type: "login", userId: payload?.user_id }
              }));
              console.log("[Wortou] OAuth login successful (via localStorage fallback)");
              resolve(tokenResponse.access_token);
            } catch (error) {
              console.error("[Wortou] Token exchange failed:", error);
              reject(new ApiRequestError2(0, {
                code: "TOKEN_EXCHANGE_FAILED",
                message: error instanceof Error ? error.message : "Token \u4EA4\u6362\u5931\u8D25"
              }));
            }
            return true;
          } else if (result.type === "wortou:oauth:error") {
            console.log("[Wortou] Found OAuth error in localStorage");
            localStorage.removeItem(resultKey);
            resolved = true;
            cleanup();
            reject(new ApiRequestError2(401, {
              code: result.error?.code || "OAUTH_ERROR",
              message: result.error?.message || "\u6388\u6743\u5931\u8D25"
            }));
            return true;
          }
        }
      } catch {
      }
      return false;
    };
    window.addEventListener("message", messageHandler);
    storageHandler = (event) => {
      if (event.key === `wortou_oauth_result_${state}` && event.newValue && !resolved) {
        console.log("[Wortou] Detected OAuth result via storage event");
        checkLocalStorageResult();
      }
    };
    window.addEventListener("storage", storageHandler);
    let popupClosedAt = null;
    const WAIT_AFTER_CLOSE = 5e3;
    timer = setInterval(async () => {
      if (!resolved) {
        const found = await checkLocalStorageResult();
        if (found) return;
      }
      if (popup.closed && !resolved) {
        if (popupClosedAt === null) {
          popupClosedAt = Date.now();
          console.log("[Wortou] Popup closed, waiting for localStorage result...");
        }
        const waitedTime = Date.now() - popupClosedAt;
        if (waitedTime >= WAIT_AFTER_CLOSE) {
          console.log("[Wortou] Timeout waiting for localStorage after popup close");
          cleanup();
          resolve(getUserToken());
        }
      }
    }, 300);
    timeoutId = setTimeout(() => {
      if (!resolved) {
        resolved = true;
        cleanup();
        resolve(getUserToken());
      }
    }, timeout);
  });
}
function openPaymentPopup(paymentUrl, options = {}) {
  const { width = 500, height = 600, timeout = 10 * 60 * 1e3 } = options;
  console.log("[Wortou Debug] openPaymentPopup called with:", { paymentUrl, options });
  if (globalConfig.popupMode === "redirect") {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const paymentCompleted = urlParams.get(WORTOU_PAYMENT_COMPLETED_KEY);
      if (paymentCompleted) {
        const completedTimestamp = parseInt(paymentCompleted, 10);
        if (completedTimestamp && Date.now() - completedTimestamp < 5 * 60 * 1e3) {
          console.log("[Wortou] Recently returned from payment (URL param), skipping redirect");
          cleanupPaymentParams();
          return Promise.resolve();
        }
      }
      const pendingPayment = sessionStorage.getItem(WORTOU_PAYMENT_PENDING_KEY);
      const pendingTimestamp = parseInt(pendingPayment || "0", 10);
      if (pendingTimestamp && Date.now() - pendingTimestamp < 5 * 60 * 1e3) {
        console.log("[Wortou] Payment pending in sessionStorage, skipping redirect to prevent loop");
        sessionStorage.removeItem(WORTOU_PAYMENT_PENDING_KEY);
        return Promise.resolve();
      }
    } catch (e) {
      console.warn("[Wortou] Failed to check payment pending state:", e);
    }
  }
  if (paymentPromise) {
    console.log("[Wortou] Payment already in progress, reusing existing promise");
    return paymentPromise;
  }
  paymentPromise = performOpenPaymentPopup(paymentUrl, { width, height, timeout });
  return paymentPromise.finally(() => {
    paymentPromise = null;
  });
}
function performOpenPaymentPopup(paymentUrl, options) {
  const { width, height, timeout } = options;
  return new Promise((resolve, reject) => {
    const state = crypto.randomUUID();
    const url = new URL(paymentUrl || globalConfig.paymentUrl);
    url.searchParams.set("state", state);
    url.searchParams.set("return_url", window.location.href);
    const gameId = getGameIdFromToken();
    console.log("[Wortou Debug] openPaymentPopup - gameId from token:", gameId);
    if (gameId) {
      url.searchParams.set("game_id", gameId.toString());
    }
    console.log("[Wortou Debug] openPaymentPopup - final URL:", url.toString());
    if (globalConfig.popupMode === "redirect") {
      try {
        sessionStorage.setItem(WORTOU_PAYMENT_PENDING_KEY, Date.now().toString());
        console.log("[Wortou] Set payment pending flag in sessionStorage");
      } catch (e) {
        console.warn("[Wortou] Failed to set payment pending state:", e);
      }
    }
    const popup = openWindow(url.toString(), "wortou_payment", { width, height });
    if (globalConfig.popupMode === "redirect") {
      return;
    }
    if (!popup) {
      globalConfig.onPopupBlocked(url.toString(), "payment");
      reject(new ApiRequestError2(0, {
        code: "POPUP_BLOCKED",
        message: "\u5F39\u7A97\u88AB\u6D4F\u89C8\u5668\u963B\u6B62\u3002\u8BF7\u70B9\u51FB\u9875\u9762\u4E0A\u7684\u5145\u503C\u6309\u94AE\uFF0C\u6216\u5141\u8BB8\u5F39\u7A97\u540E\u91CD\u8BD5",
        action: "show_payment_popup"
      }));
      return;
    }
    let resolved = false;
    let pollTimer;
    let timeoutId;
    const messageHandler = (event) => {
      const paymentOrigin = new URL(globalConfig.paymentUrl).origin;
      const platformOrigin = new URL(globalConfig.platformUrl).origin;
      const allowedOrigins = [
        paymentOrigin,
        platformOrigin,
        "feedscription.com"
      ];
      const isAllowed = allowedOrigins.some(
        (origin) => event.origin === origin || event.origin.includes(origin)
      );
      if (!isAllowed) {
        return;
      }
      const data = event.data;
      if (data?.type === "wortou:request_token") {
        const token = getUserToken();
        if (token && popup && !popup.closed) {
          popup.postMessage({
            type: "wortou:token",
            token
          }, paymentOrigin);
        }
        return;
      }
      if (data?.type === "wortou:payment:success" || data?.type === "wortou:purchase:success") {
        if (data.data?.state && data.data.state !== state) {
          console.warn("[Wortou] Payment/Purchase state mismatch, ignoring");
          return;
        }
        if (gameId && data.data?.gameId && data.data.gameId !== gameId) {
          console.warn("[Wortou] Purchase gameId mismatch! Expected:", gameId, "Got:", data.data.gameId);
        }
        resolved = true;
        cleanup();
        resolve();
      }
      if (data?.type === "wortou:payment:error" || data?.type === "wortou:purchase:error") {
        resolved = true;
        cleanup();
        reject(new ApiRequestError2(402, {
          code: "PAYMENT_FAILED",
          message: data.error?.message || "\u652F\u4ED8\u5931\u8D25"
        }));
      }
    };
    const cleanup = () => {
      clearInterval(pollTimer);
      clearTimeout(timeoutId);
      window.removeEventListener("message", messageHandler);
      if (popup && !popup.closed) {
        popup.close();
      }
    };
    window.addEventListener("message", messageHandler);
    pollTimer = setInterval(() => {
      if (popup.closed && !resolved) {
        resolved = true;
        cleanup();
        resolve();
      }
    }, 500);
    timeoutId = setTimeout(() => {
      if (!resolved) {
        resolved = true;
        cleanup();
        resolve();
      }
    }, timeout);
  });
}
async function showLoginPopup(options) {
  await openLoginPopupWithToken(options);
}
async function showPaymentPopup(paymentUrl, options) {
  if (HIDE_BILLING) {
    console.warn("[Wortou] showPaymentPopup is disabled when HIDE_BILLING=true");
    return;
  }
  await openPaymentPopup(paymentUrl, options);
}
async function ensureLoggedIn(options) {
  if (isLoggedIn()) {
    return true;
  }
  try {
    const token = await openLoginPopupWithToken(options);
    return !!token;
  } catch (err) {
    if (err instanceof ApiRequestError2 && err.response.code === "POPUP_BLOCKED") {
      throw err;
    }
    return false;
  }
}
async function ensureBalance(paymentUrl, options) {
  if (HIDE_BILLING) {
    console.warn("[Wortou] ensureBalance is disabled when HIDE_BILLING=true");
    return true;
  }
  try {
    await openPaymentPopup(paymentUrl, options);
    return true;
  } catch (err) {
    if (err instanceof ApiRequestError2 && err.response.code === "POPUP_BLOCKED") {
      throw err;
    }
    return false;
  }
}
async function apiRequest(url, init2, options = {}) {
  const {
    autoLogin = globalConfig.autoLogin,
    autoPayment = globalConfig.autoPayment,
    maxRetries = globalConfig.maxRetries,
    timeout = globalConfig.timeout,
    headers: customHeaders
  } = options;
  await ensureValidToken();
  let hasTriedRefresh = false;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    try {
      const headers = new Headers(init2?.headers);
      if (customHeaders) {
        for (const [key, value] of Object.entries(customHeaders)) {
          headers.set(key, value);
        }
      }
      if (!headers.has("Content-Type") && init2?.body) {
        headers.set("Content-Type", "application/json");
      }
      const userToken = getUserToken();
      if (userToken) {
        headers.set("Authorization", `Bearer ${userToken}`);
        headers.set("X-Wortou-User-Token", userToken);
        if (process.env.NODE_ENV === "development" || typeof window !== "undefined" && window.__WORTOU_DEBUG__) {
          console.log("[Wortou Debug] apiRequest: Attaching token to request", userToken.substring(0, 20) + "...");
        }
      } else {
        if (process.env.NODE_ENV === "development" || typeof window !== "undefined" && window.__WORTOU_DEBUG__) {
          console.log("[Wortou Debug] apiRequest: No token available, request will be unauthenticated");
        }
      }
      const response = await fetch(url, {
        ...init2,
        headers,
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      if (response.status === 401) {
        if (!hasTriedRefresh && getRefreshToken()) {
          hasTriedRefresh = true;
          console.log("[Wortou] Got 401, attempting token refresh...");
          const newToken = await refreshAccessToken();
          if (newToken) {
            console.log("[Wortou] Token refreshed after 401, retrying request...");
            continue;
          }
        }
        console.log("[Wortou] Token invalid, clearing and showing login popup...");
        clearUserToken();
        if (autoLogin) {
          try {
            const data = await response.clone().json();
            if (data.action === "show_login_popup") {
              await openLoginPopupWithToken();
              continue;
            }
          } catch {
            await openLoginPopupWithToken();
            continue;
          }
        }
      }
      if (response.status === 402 && autoPayment) {
        try {
          const data = await response.clone().json();
          if (data.action === "show_payment_popup") {
            await openPaymentPopup(data.paymentUrl);
            continue;
          }
        } catch {
          await openPaymentPopup();
          continue;
        }
      }
      if (!response.ok) {
        let errorResponse;
        try {
          errorResponse = await response.json();
        } catch {
          errorResponse = {
            code: "UNKNOWN_ERROR",
            message: `HTTP ${response.status}: ${response.statusText}`
          };
        }
        throw new ApiRequestError2(response.status, errorResponse);
      }
      const contentType = response.headers.get("content-type");
      if (contentType?.includes("application/json")) {
        return await response.json();
      }
      return await response.text();
    } catch (err) {
      clearTimeout(timeoutId);
      if (err instanceof ApiRequestError2) {
        throw err;
      }
      if (err instanceof Error && err.name === "AbortError") {
        throw new ApiRequestError2(408, {
          code: "TIMEOUT",
          message: "\u8BF7\u6C42\u8D85\u65F6"
        });
      }
      throw new ApiRequestError2(0, {
        code: "NETWORK_ERROR",
        message: err instanceof Error ? err.message : "\u7F51\u7EDC\u9519\u8BEF"
      });
    }
  }
  throw new ApiRequestError2(0, {
    code: "MAX_RETRIES_EXCEEDED",
    message: "\u91CD\u8BD5\u6B21\u6570\u5DF2\u8FBE\u4E0A\u9650"
  });
}
async function apiPost(url, body, options) {
  return apiRequest(
    url,
    {
      method: "POST",
      body: JSON.stringify(body)
    },
    options
  );
}
async function apiGet(url, options) {
  return apiRequest(url, { method: "GET" }, options);
}
async function apiPut(url, body, options) {
  return apiRequest(
    url,
    {
      method: "PUT",
      body: JSON.stringify(body)
    },
    options
  );
}
async function apiDelete(url, options) {
  return apiRequest(url, { method: "DELETE" }, options);
}
function createApiClient(clientOptions = {}) {
  const { baseUrl = "", ...defaultOptions } = clientOptions;
  const resolveUrl = (url) => {
    if (url.startsWith("http://") || url.startsWith("https://")) {
      return url;
    }
    return `${baseUrl}${url}`;
  };
  const mergeOptions = (options) => ({
    ...defaultOptions,
    ...options,
    headers: {
      ...defaultOptions.headers,
      ...options?.headers
    }
  });
  return {
    request: (url, init2, options) => apiRequest(resolveUrl(url), init2, mergeOptions(options)),
    get: (url, options) => apiGet(resolveUrl(url), mergeOptions(options)),
    post: (url, body, options) => apiPost(resolveUrl(url), body, mergeOptions(options)),
    put: (url, body, options) => apiPut(resolveUrl(url), body, mergeOptions(options)),
    delete: (url, options) => apiDelete(resolveUrl(url), mergeOptions(options))
  };
}
async function checkPurchaseStatus(options) {
  const url = `${globalConfig.apiBaseUrl}/purchases/current`;
  const response = await apiGet(url, {
    ...options,
    // 默认开启自动登录，因为查询购买状态需要认证
    autoLogin: options?.autoLogin ?? true
  });
  return response;
}
async function hasPurchased(options) {
  try {
    const status = await checkPurchaseStatus(options);
    return status.purchased;
  } catch {
    return false;
  }
}
var COMPRESSION_CONFIG = {
  /** Minimum size in bytes to trigger automatic compression */
  threshold: 100 * 1024,
  // 100KB
  /** Compression algorithm */
  algorithm: "gzip"
};
function isCompressionSupported() {
  return typeof CompressionStream !== "undefined";
}
async function compressData(data) {
  const encoder = new TextEncoder();
  const inputData = encoder.encode(data);
  const stream = new CompressionStream("gzip");
  const writer = stream.writable.getWriter();
  const reader = stream.readable.getReader();
  writer.write(inputData);
  writer.close();
  const chunks = [];
  let totalLength = 0;
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    totalLength += value.length;
  }
  const result = new Uint8Array(totalLength);
  let offset = 0;
  for (const chunk of chunks) {
    result.set(chunk, offset);
    offset += chunk.length;
  }
  return result;
}
function uint8ArrayToBase64(data) {
  let binary = "";
  for (let i = 0; i < data.length; i++) {
    binary += String.fromCharCode(data[i]);
  }
  return btoa(binary);
}
var SaveVersionConflictError = class _SaveVersionConflictError extends Error {
  constructor(expectedVersion, currentVersion) {
    super(`Version conflict: expected ${expectedVersion}, server has ${currentVersion}`);
    /** Error code */
    this.code = "SAVE_VERSION_CONFLICT";
    this.name = "SaveVersionConflictError";
    this.expectedVersion = expectedVersion;
    this.currentVersion = currentVersion;
    Object.setPrototypeOf(this, _SaveVersionConflictError.prototype);
  }
};
async function saveGame(slotId, data, options) {
  const url = `${globalConfig.apiBaseUrl}/saves/${encodeURIComponent(slotId)}`;
  const jsonData = JSON.stringify(data);
  const dataSize = new TextEncoder().encode(jsonData).length;
  const shouldCompress = options?.compress ?? (dataSize > COMPRESSION_CONFIG.threshold && isCompressionSupported());
  try {
    if (shouldCompress && isCompressionSupported()) {
      const compressedData = await compressData(jsonData);
      const base64Data = uint8ArrayToBase64(compressedData);
      if (process.env.NODE_ENV === "development" || typeof window !== "undefined" && window.__WORTOU_DEBUG__) {
        const ratio = ((1 - compressedData.length / dataSize) * 100).toFixed(1);
        console.log(`[Wortou Debug] saveGame: Compressing data ${dataSize} -> ${compressedData.length} bytes (${ratio}% reduction)`);
      }
      return await apiPut(url, {
        saveData: base64Data,
        name: options?.name,
        expectedVersion: options?.expectedVersion,
        _compressed: true,
        _compression: COMPRESSION_CONFIG.algorithm,
        _originalSize: dataSize
      });
    } else {
      return await apiPut(url, {
        saveData: data,
        name: options?.name,
        expectedVersion: options?.expectedVersion
      });
    }
  } catch (error) {
    if (error instanceof ApiRequestError2 && error.status === 409) {
      const details = error.response;
      throw new SaveVersionConflictError(
        details.details?.expectedVersion ?? options?.expectedVersion ?? 0,
        details.details?.currentVersion ?? 0
      );
    }
    throw error;
  }
}
async function loadGame(slotId) {
  const url = `${globalConfig.apiBaseUrl}/saves/${encodeURIComponent(slotId)}`;
  try {
    return await apiGet(url);
  } catch (error) {
    if (error instanceof ApiRequestError2 && error.status === 404) {
      return null;
    }
    throw error;
  }
}
async function listSaves() {
  const url = `${globalConfig.apiBaseUrl}/saves`;
  return apiGet(url);
}
async function deleteSave(slotId) {
  const url = `${globalConfig.apiBaseUrl}/saves/${encodeURIComponent(slotId)}`;
  await apiDelete(url);
}
async function getSaveUsage() {
  const { usage } = await listSaves();
  return usage;
}
function autoHandleOAuthHashCallback() {
  if (typeof window === "undefined") return;
  const hash = window.location.hash;
  if (!hash || !hash.includes("wortou_oauth_callback=1")) {
    return;
  }
  const params = new URLSearchParams(hash.slice(1));
  const code = params.get("code");
  const state = params.get("state");
  if (!code || !state) {
    return;
  }
  console.log("[Wortou] Handling OAuth callback from hash redirect (popup fallback)");
  if (window.history && window.history.replaceState) {
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
  }
  try {
    const resultKey = `wortou_oauth_result_${state}`;
    localStorage.setItem(resultKey, JSON.stringify({
      type: "wortou:oauth:code",
      code,
      state,
      timestamp: Date.now()
    }));
    console.log("[Wortou] OAuth result stored in localStorage");
  } catch (e) {
    console.error("[Wortou] Failed to store OAuth result:", e);
  }
  if (window.opener) {
    try {
      window.opener.postMessage({
        type: "wortou:oauth:code",
        code,
        state
      }, "*");
      console.log("[Wortou] OAuth result sent via postMessage");
    } catch {
    }
  }
  setTimeout(() => {
    try {
      window.close();
    } catch {
    }
  }, 100);
}
function cleanupStaleOAuthResults() {
  if (typeof window === "undefined" || typeof localStorage === "undefined") return;
  try {
    const keysToRemove = [];
    const now = Date.now();
    const MAX_AGE = 10 * 60 * 1e3;
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith("wortou_oauth_result_")) {
        try {
          const value = localStorage.getItem(key);
          if (value) {
            const data = JSON.parse(value);
            if (data.timestamp && now - data.timestamp > MAX_AGE) {
              keysToRemove.push(key);
            }
          }
        } catch {
          keysToRemove.push(key);
        }
      }
    }
    for (const key of keysToRemove) {
      localStorage.removeItem(key);
    }
    if (keysToRemove.length > 0) {
      console.log(`[Wortou] Cleaned up ${keysToRemove.length} stale OAuth results`);
    }
  } catch (e) {
  }
}
if (typeof window !== "undefined") {
  try {
    autoHandleOAuthHashCallback();
  } catch (e) {
    console.error("[Wortou] Error in autoHandleOAuthHashCallback:", e);
  }
  try {
    cleanupStaleOAuthResults();
  } catch {
  }
}
export {
  ApiRequestError2 as ApiRequestError,
  SaveVersionConflictError,
  apiDelete,
  apiGet,
  apiPost,
  apiPut,
  apiRequest,
  checkLoginStatus,
  checkPurchaseStatus,
  cleanupPaymentParams,
  clearUserToken,
  closePreOpenedWindow,
  configure,
  createApiClient,
  createApiRequest,
  deleteSave,
  ensureBalance,
  ensureLoggedIn,
  getConfig,
  getLoginUrl,
  getOAuthLoginUrl,
  getSaveUsage,
  getUserToken,
  handleLoginCallback,
  handleOAuthCallback,
  handleRedirectCallback,
  hasPurchased,
  init,
  isLoggedIn,
  listSaves,
  loadGame,
  openLoginPopupWithToken as openPopup,
  parseJWT,
  preOpenWindow,
  resetConfig,
  saveGame,
  showLoginPopup,
  showPaymentPopup,
  startOAuthLogin
};
//# sourceMappingURL=index.js.map